import React, { useEffect, useMemo, useState } from 'react';
import UseLangLstQuery from 'Apis/LangTxtInqApi';
import { useLocation, useOutletContext } from "react-router-dom";
import MgpStore from 'Stores/Store';
import { useSelector } from 'react-redux';
import 'Assets/css/cupn.css';
import 'Assets/css/Shop.css';
import GameCupnApi from 'Apis/GameCupnApi';
import useRecaptcha from 'Utils/useRecaptcha';
import EtcCtxtApi from 'Apis/EtcCtxtApi';
import ShopBnrLstApi from 'Apis/ShopBnrLstApi';
import MembGameSvcMnyApi from 'Apis/MembGameSvcMnyApi';
import ApiResultModal from 'Components/ApiResultModal';
import CtntCategoryMenu from 'Components/CtntCategoryMenu';
import ShopSlideUi, { ShopSlideItem } from 'Views/Shop/ShopSlideUi';
import MyCashSection from 'Views/Shop/components/MyCashSection';

interface CashState {
    loginYn: string;
    nickname: string;
    rcmnCd: string;
    gameMnyNm: string;
    loginId: string;
    membNo: number;
    totMny: number;
    paidMny: number;
    freeMny: number;
}

const createShopBannerDummyImage = (index: number) =>
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="1370" height="222" viewBox="0 0 1370 222"><rect width="1370" height="222" fill="#E6EAF0"/><rect x="1" y="1" width="1368" height="220" fill="none" stroke="#C7D0DB" stroke-width="2"/><text x="50%" y="44%" dominant-baseline="middle" text-anchor="middle" fill="#6D7A8A" font-family="Arial, sans-serif" font-size="34" font-weight="700">SHOP BANNER DUMMY ${index}</text><text x="50%" y="62%" dominant-baseline="middle" text-anchor="middle" fill="#8795A8" font-family="Arial, sans-serif" font-size="24" font-weight="600">1370 x 222</text></svg>`
    );

const GameCupnUi = () => {
    const location = useLocation();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const getGameSvcCd = query.get("gameSvcCd") || "BBO";
    const { gameMainPageData }: any = useOutletContext();
    const { data: langData }: any = UseLangLstQuery();
    const storeLang = useSelector((state: any) => state.LangSetState?.Lang);
    const loginInfoState = useSelector((state: any) => state.LoginInfoState?.LoginInfo?.apiBody);
    const [shopBannerState, setShopBannerState] = useState<any[]>([]);
    const [myCashState, setMyCashState] = useState<CashState | null>(null);
    const [isBrickTooltipOpen, setIsBrickTooltipOpen] = useState(false);
    const [cupnCdState, setCupnCdState] = useState("");
    const [membUUIDState, setMembUUIDState] = useState<string | null>(null);
    const [redeemNotiCtxtState, setRedeemNotiCtxtState] = useState("");
    const [resultModalOpen, setResultModalOpen] = useState(false);
    const [resultModalMessage, setResultModalMessage] = useState("");
    const { getToken } = useRecaptcha();

    const firstLetter = (loginInfoState?.loginId || myCashState?.loginId || myCashState?.nickname || '').charAt(0).toUpperCase();

    const shopSlideItems = useMemo<ShopSlideItem[]>(() => {
        const mappedSlides = shopBannerState
            .filter((banner: any) => !!banner?.bnrFileUrl || !!banner?.moBnrFileUrl || !!banner?.mobBnrFileUrl || !!banner?.mobileBnrFileUrl)
            .map((banner: any, idx) => {
                const detectedMobileBannerUrl = Object.entries(banner || {}).find(([key, value]) => {
                    if (typeof value !== "string" || !value.trim()) {
                        return false;
                    }
                    return /(mo|mob|mobile).*(bnr|banner).*(url|file)|(bnr|banner).*(mo|mob|mobile).*(url|file)/i.test(key);
                })?.[1] as string | undefined;

                return {
                    id: `${banner.gameSvcCd || "SHOP"}-${idx}`,
                    gameSvcCd: banner.gameSvcCd,
                    aplyStrtDtime: banner.aplyStrtDtime,
                    aplyStopDtime: banner.aplyStopDtime,
                    bnrTitl: banner.bnrTitl,
                    bnrCtxt: banner.bnrCtxt,
                    bnrDesc: banner.bnrDesc,
                    lnkUrlAddr: banner.lnkUrlAddr,
                    lnkUrlTxt: banner.lnkUrlTxt,
                    bnrFileUrl: banner.bnrFileUrl || "",
                    moBnrFileUrl: banner.moBnrFileUrl || banner.mobBnrFileUrl || banner.mobileBnrFileUrl || detectedMobileBannerUrl || "",
                    lnkTrgtCd: banner.lnkTrgtCd,
                } as ShopSlideItem;
            });

        if (mappedSlides.length > 0) {
            return mappedSlides;
        }

        return Array.from({ length: 4 }, (_, idx) => ({
            id: `shop-banner-dummy-${idx + 1}`,
            bnrFileUrl: createShopBannerDummyImage(idx + 1),
            moBnrFileUrl: createShopBannerDummyImage(idx + 1),
            bnrTitl: `shop-banner-dummy-${idx + 1}-1370x222`,
        }));
    }, [shopBannerState]);

    const currentParams = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return {
            currentCtntCtgrCd: (params.get("ctntKindCd") || params.get("shopKindCd") || "").toUpperCase(),
        };
    }, [location.search]);

    const currentMainMenu = useMemo(() => {
        const isCupnRoute = /^\/gamepage\/gamecupn(\/|$)/i.test(location.pathname);

        if (isCupnRoute) {
            return gameMainPageData?.menu?.find((menu: any) => {
                const menuUrl = String(menu?.url || '').toLowerCase();
                const menuCd = String(menu?.menuCd || '').toUpperCase();

                if (menuUrl.includes('/gamepage/shoplst') || menuCd === 'SHOP') {
                    return true;
                }

                return menu?.subMenuLst?.some((sub: any) => {
                    const subUrl = String(sub?.url || '').toLowerCase();
                    const subCd = String(sub?.menuCd || '').toUpperCase();
                    const urlParams = new URLSearchParams(sub?.url?.split('?')[1] || '');
                    const subCtntKindCd = (urlParams.get('ctntKindCd') || '').toUpperCase();
                    return subUrl.includes('/gamepage/shoplst') || subCd === 'SHOP' || subCtntKindCd === 'SHOP';
                });
            });
        }

        return gameMainPageData?.menu?.find((menu: any) => {
            if (currentParams.currentCtntCtgrCd) {
                return menu.subMenuLst?.some((sub: any) => {
                    const urlParams = new URLSearchParams(sub.url?.split('?')[1] || '');
                    const subCtntKindCd = (urlParams.get('ctntKindCd') || sub.menuCd || '').toUpperCase();
                    return subCtntKindCd === currentParams.currentCtntCtgrCd;
                });
            }

            const currentPath = location.pathname.toLowerCase();
            return menu.subMenuLst?.some((sub: any) => {
                const subUrl = String(sub.url || '').toLowerCase();
                return subUrl.includes(currentPath);
            });
        });
    }, [currentParams.currentCtntCtgrCd, gameMainPageData, location.pathname]);

    const ctntMenuData = useMemo(() => {
        const isCupnRoute = /^\/gamepage\/gamecupn(\/|$)/i.test(location.pathname);

        return currentMainMenu?.subMenuLst?.map((subMenu: any) => {
            const subPath = String(subMenu.url || '').split('?')[0].toLowerCase();
            const urlParams = new URLSearchParams(subMenu.url?.split('?')[1] || '');
            const ctntKindCd = (urlParams.get('ctntKindCd') || subMenu.menuCd || '').toUpperCase();
            const resolvedCode = isCupnRoute && subPath.includes('/gamepage/gamecupn')
                ? 'GAMECUPN'
                : ctntKindCd;

            return {
                Link: subMenu.url || '#',
                Txt: subMenu.menuNm,
                Display: subMenu.useYn,
                Code: resolvedCode,
            };
        }) || [];
    }, [currentMainMenu, location.pathname]);

    const visibleMenus = ctntMenuData.filter((menu: any) => menu.Display === "Y");

    const activeMenuCode = useMemo(() => {
        const isCupnRoute = /^\/gamepage\/gamecupn(\/|$)/i.test(location.pathname);
        if (isCupnRoute) {
            const cupnMenu = visibleMenus.find((menu: any) =>
                String(menu.Link || '').toLowerCase().includes('/gamepage/gamecupn')
            );
            if (cupnMenu) {
                return cupnMenu.Code;
            }
        }

        const foundByPath = visibleMenus.find((menu: any) =>
            String(menu.Link || '').toLowerCase().includes(location.pathname.toLowerCase())
        );
        return foundByPath?.Code || currentParams.currentCtntCtgrCd || 'MNY';
    }, [currentParams.currentCtntCtgrCd, visibleMenus, location.pathname]);

    const CupnCdHandler = (event: any) => {
        setCupnCdState(event.currentTarget.value);
    }

    const MembUUIDHandler = (event: any) => {
        setMembUUIDState(event.currentTarget.value);
    }

    const CupnReqFunc = async () => {
        try {
            const token = await getToken('cupn');
            const resp = await GameCupnApi(getGameSvcCd, cupnCdState, membUUIDState, token);
            switch (resp.apiHdr.code) {
                case "GAME0000":
                    setResultModalMessage(langData && langData.GAME_CUPN_USE_RSLT_DESC_00 || "리딤코드가 정상적으로 성공적으로 사용되었습니다.");
                    break;
                case "GAME3106":
                    setResultModalMessage(langData && langData.GAME_CUPN_USE_RSLT_DESC_01 || "리딤코드 사용에 실패했습니다. 입력하신 코드를 다시 한번 확인해주세요.");
                    break;
                case "GAME3107":
                    setResultModalMessage(langData && langData.GAME_CUPN_USE_RSLT_DESC_02 || "이미 사용한 리딤코드입니다. <br />입력하신 코드를 다시 한번 확인해주세요.");
                    break;
                case "GAME3108":
                    setResultModalMessage(langData && langData.GAME_CUPN_USE_RSLT_DESC_03 || "유효하지 않은 리딤코드입니다. <br />입력하신 코드를 다시 한번 확인해주세요.");
                    break;
                case "GAME3109":
                    setResultModalMessage(langData && langData.GAME_CUPN_USE_RSLT_DESC_04 || "사용기간이 만료된 리딤코드입니다. <br />입력하신 코드를 다시 한번 확인해주세요.");
                    break;
                case "GAME3119":
                    setResultModalMessage(resp.apiHdr.Msg || "해당 쿠폰은 등록 한도를 초과했습니다.");
                    break;
                case "BEPR4004":
                    setResultModalMessage(langData && langData.GAME_CUPN_USE_RSLT_DESC_05 || "존재하지 않는 리딤코드입니다. <br />입력하신 코드를 다시 한번 확인해주세요.");
                    break;
                default:
                    setResultModalMessage(`${langData && langData.GAME_CUPN_USE_RSLT_DESC_01 || "리딤코드 사용에 실패했습니다. <br />입력하신 코드를 다시 한번 확인해주세요."}${resp.apiHdr.code}`);
                    break;
            }
            setResultModalOpen(true);
        } catch (error) {
            setResultModalMessage(`${langData && langData.COMM_MAIN_RSLT_DESC_00 || "오류가 발생했습니다."}`);
            setResultModalOpen(true);
        }
    }

    useEffect(() => {
        EtcCtxtApi("CTNT_KIND_ETC", "REDEEM_GUIDE", "BBO").then((resp) => {
            if (resp.apiHdr.code === "COMM0000") {
                setRedeemNotiCtxtState(resp.apiBody.ctxt);
            }
        });
    }, [MgpStore.getState().LangSetState.Lang, gameMainPageData]);

    useEffect(() => {
        ShopBnrLstApi(getGameSvcCd)
            .then((resp) => {
                const banners = Array.isArray(resp?.apiBody?.bnrLst) ? resp.apiBody.bnrLst : [];
                setShopBannerState(banners);
            })
            .catch(() => {
                setShopBannerState([]);
            });
    }, [getGameSvcCd, storeLang]);

    useEffect(() => {
        MembGameSvcMnyApi(getGameSvcCd)
            .then((resp) => {
                setMyCashState(resp.apiBody);
            })
            .catch(() => {
                setMyCashState(null);
            });
    }, [getGameSvcCd, storeLang]);

    useEffect(() => {
        const handleOutsideClose = (event: MouseEvent | TouchEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest(".brickInfoTooltipWrap")) {
                setIsBrickTooltipOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClose);
        document.addEventListener("touchstart", handleOutsideClose, { passive: true });

        return () => {
            document.removeEventListener("mousedown", handleOutsideClose);
            document.removeEventListener("touchstart", handleOutsideClose);
        };
    }, []);

    const handleBrickInfoToggle = () => {
        if (typeof window !== "undefined" && window.matchMedia("(hover: none), (pointer: coarse)").matches) {
            setIsBrickTooltipOpen((prev) => !prev);
        }
    };

    return (
        <div className='BgW pt140 shopLstBg gamePageWrap'>
            <ApiResultModal
                open={resultModalOpen}
                message={resultModalMessage}
                onClose={() => setResultModalOpen(false)}
                confirmText={langData && langData.COMM_MAIN_CHK_MAIN_BTN_00}
            />
            <ShopSlideUi
                className="container eventSlideWrap"
                slides={shopSlideItems}
                autoplayDelay={10000}
                gameSvcCd={getGameSvcCd}
                langData={langData}
            />
            <MyCashSection
                myCashState={myCashState}
                loginInfoState={loginInfoState}
                firstLetter={firstLetter}
                isBrickTooltipOpen={isBrickTooltipOpen}
                onToggleBrickInfo={handleBrickInfoToggle}
                langData={langData}
                gameSvcCd={getGameSvcCd}
                shopKindCd={activeMenuCode || 'MNY'}
            />
            <div className='shopContainer'>
                <div className='cupnInputBnrWrap'>
                    {visibleMenus.length > 0 && (
                        <div className="ctntCtgrTitlTxt">
                            <CtntCategoryMenu
                                menus={visibleMenus}
                                activeCode={activeMenuCode}
                                className="ctntCtgrMenu"
                            />
                            <a href={`/GamePage/GamePrbb?gameSvcCd=${encodeURIComponent(getGameSvcCd)}`} target="_blank" rel="noopener noreferrer">
                                <p>{langData && langData.GLB_PRBB_LINK_BTN}</p>
                                <img src={process.env.PUBLIC_URL + "/images/blankIcon.png"} alt="" />
                            </a>
                        </div>
                    )}
                    <div className='cupnInputWrap'>
                        <h2>{langData && langData.GAME_CUPN_PAGE_TITL_00}</h2>
                        <div>
                            {gameMainPageData?.gamePltf !== "PC" ?
                                <div className='cupnInputLi'>
                                    <p>회원 번호</p>
                                    <div className='cupnInputBg'>
                                        <input placeholder={`${langData && langData.GAME_CUPN_MEMB_INPUT_DESC_00}`} type='text' className='cupnInput' autoComplete="off" onChange={MembUUIDHandler}></input>
                                    </div>
                                </div>
                                :
                                <></>
                            }
                            <div className='cupnInputLi'>
                                <p>{langData && langData.GAME_CUPN_CUPN_DESC_00}</p>
                                <div className='cupnInputBg'>
                                    <input placeholder={`${langData && langData.GAME_CUPN_CUPN_INPUT_DESC_00}`} type='text' className='cupnInput' autoComplete="off" onChange={CupnCdHandler}></input>
                                </div>
                            </div>
                        </div>
                        <div>
                            <button onClick={CupnReqFunc}>
                                <p>{langData && langData.GAME_CUPN_MAIN_BTN_00}</p>
                            </button>
                        </div>
                    </div>
                    <div className="emailAuthAccordion mt50">
                        <details>
                            <summary>
                                <svg className="chev" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="#222" />
                                </svg>
                                <span>{langData?.GLB_NOTI_TITL_00 || "주의사항"}</span>
                            </summary>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: redeemNotiCtxtState,
                                }}
                            >
                            </div>
                        </details>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default GameCupnUi;